import { useLingui } from "@lingui/react/macro";
import { Button } from "@rakazo/ui-web";
import { NativeSelect, NativeSelectOption } from "@rakazo/ui-web/components/ui/native-select";
import { Popover, PopoverContent, PopoverTrigger } from "@rakazo/ui-web/components/ui/popover";
import { Headphones } from "lucide-react";
import { useId, useSyncExternalStore } from "react";
import {
  canChooseCallOutput,
  chooseCallInput,
  chooseCallOutput,
  getCallAudioDevices,
  refreshCallAudioDevices,
  subscribeCallAudioDevices,
} from "../../lib/call-audio-route";

export function CallAudioPicker() {
  const { t } = useLingui();
  const outputId = useId();
  const inputId = useId();
  const state = useSyncExternalStore(subscribeCallAudioDevices, getCallAudioDevices);
  return (
    <Popover
      onOpenChange={(open) => {
        if (open) void refreshCallAudioDevices();
      }}
    >
      <PopoverTrigger
        render={
          <Button variant="secondary" size="icon-lg" className="size-14 rounded-full md:size-12" />
        }
        aria-label={t`Audio devices`}
      >
        <Headphones className="size-5" />
      </PopoverTrigger>
      <PopoverContent side="top">
        {canChooseCallOutput() ? (
          <div className="flex flex-col gap-1">
            <label htmlFor={outputId}>{t`Audio output`}</label>
            <NativeSelect
              id={outputId}
              className="w-full"
              value={state.outputId}
              onChange={(event) => {
                void chooseCallOutput(event.target.value).catch(() => undefined);
              }}
            >
              <NativeSelectOption value="">{t`System default`}</NativeSelectOption>
              {state.devices
                .filter(
                  (device) =>
                    device.kind === "audiooutput" &&
                    device.deviceId &&
                    device.deviceId !== "default",
                )
                .map((device, index) => (
                  <NativeSelectOption key={device.deviceId} value={device.deviceId}>
                    {device.label || t`Speaker ${index + 1}`}
                  </NativeSelectOption>
                ))}
            </NativeSelect>
          </div>
        ) : (
          <p className="text-muted-foreground">{t`Choose audio output in your system sound settings.`}</p>
        )}
        <div className="flex flex-col gap-1">
          <label htmlFor={inputId}>{t`Microphone`}</label>
          <NativeSelect
            id={inputId}
            className="w-full"
            value={state.inputId}
            onChange={(event) => {
              void chooseCallInput(event.target.value).catch(() => undefined);
            }}
          >
            <NativeSelectOption value="">{t`System default`}</NativeSelectOption>
            {state.devices
              .filter(
                (device) =>
                  device.kind === "audioinput" && device.deviceId && device.deviceId !== "default",
              )
              .map((device, index) => (
                <NativeSelectOption key={device.deviceId} value={device.deviceId}>
                  {device.label || t`Microphone ${index + 1}`}
                </NativeSelectOption>
              ))}
          </NativeSelect>
        </div>
        {state.error ? (
          <p role="alert" className="text-destructive">{t`Could not change audio device.`}</p>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
